import classNames from 'classnames/bind';
import styles from './CardForm.module.css';
import { Controller, useFormContext } from 'react-hook-form';

import {
  ACCEPT_ALL_TERMS,
  AUTOMATIC_PAYMENT_DESC,
  BIRTHDATE,
  BIRTHDATE_SIX_DIGITS_PLACEHOLDER,
  CARD_BUSINESS_REGISTRATION_NUMBER,
  CARD_EXPIRATION_DATE,
  CARD_NUMBER,
  CARD_PIN_PREFIX,
  MOBILE_NUMBER,
  EMAIL,
  MONTHLY_INSTALLMENTS,
  MORE_DETAILS_SERVICE_REFERENCE,
  PASSWORD,
  PAYMENT,
  PREV,
  TERMS,
  ENTER_MOBILE_NUMBER,
  ENTER_EMAIL_MESSAGE,
  CARD_NUMBER_LENGTH_ALERT,
  INSTALLMENTS,
  NAME,
  ENTER_NAME,
  REFUND_INFORMATION,
} from '../../constants';
import Icon from '../Icon';
import TermsItem from '../../pages/Login/SignUp/TermsItem';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AgreeList, PaymentMembershipInfo } from '../../types/membership';
import SelectBox from '../SelectBox';
import { limitMaxLen } from '../../utils/util';
import { year } from '../../utils/date';
import {
  useSubmitPaymentAdditionalPersonnelMutation,
  useSubmitPaymentInfoMutation,
  useSubmitPaymentMutation,
} from '../../services/membership';
import { mainApiSlice } from '../../services/main';
import { useAppDispatch } from '../../hooks/useRedux';
import { storageChanged } from '../../features/storageSlice';

interface CardForm {
  name?: string;
  isPersonnel?: boolean;
  agreeList: AgreeList;
  onSubmitCb: (res: any) => void;
  membershipInfo?: PaymentMembershipInfo;
}

export default function CardForm({
  name,
  isPersonnel,
  agreeList,
  onSubmitCb,
  membershipInfo,
}: CardForm) {
  const cx = classNames.bind(styles);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    control,
    register,
    getValues,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    formState: { isValid, errors },
  } = useFormContext();

  const [isAgreeAll, setIsAgreeAll] = useState(false);
  const [isCheckedAgreeList, setIsCheckedAgreeList] = useState([
    false,
    false,
    false,
  ]);

  const [submitPayment] = useSubmitPaymentMutation();
  const [submitPaymentAddPersonnel] =
    useSubmitPaymentAdditionalPersonnelMutation();
  const [submitPaymentInfo] = useSubmitPaymentInfoMutation();
  const [getStorage] = mainApiSlice.useLazyGetStorageQuery();

  const getIsCheckedAgreeList = () =>
    getValues('agreeCdList').map((code: string) => code !== '');

  const agreeAllTerms = () => {
    agreeList.map((item, i) => {
      setValue(`agreeCdList.${i}`, !isAgreeAll ? item.agreeCd : '');
    });
    setIsAgreeAll(!isAgreeAll);
    setIsCheckedAgreeList(getIsCheckedAgreeList());
  };

  const setAgreementValue = (name: string, value: string) => {
    setValue(name, value);
    setIsAgreeAll(!getIsCheckedAgreeList().includes(false));
    setIsCheckedAgreeList(getIsCheckedAgreeList());
  };

  const checkCardNumberLen = () => {
    const cardNo1 = getValues('paymentInfo.cardNo1');
    const cardNo2 = getValues('paymentInfo.cardNo2');
    const cardNo3 = getValues('paymentInfo.cardNo3');
    const cardNo4 = getValues('paymentInfo.cardNo4');

    if (
      cardNo1 &&
      cardNo2 &&
      cardNo3 &&
      cardNo4 &&
      (cardNo1 + cardNo2 + cardNo3 + cardNo4).length < 15
    ) {
      setError('paymentInfo.cardNo1', {
        type: 'custom',
        message: CARD_NUMBER_LENGTH_ALERT,
      });
    } else {
      (errors as any).paymentInfo?.cardNo1 &&
        clearErrors('paymentInfo.cardNo1');
    }
  };

  const onSubmit = async (data: any) => {
    let submitData = {
      ...data,
      aitool: +data.aitool
        ? { serviceList: [{ serviceId: +data.aitool, capacity: 1 }] }
        : null,
    };
    delete submitData.agreeCdList;
    if (isPersonnel) {
      delete submitData.aitool;
      delete submitData.membershipInfo;
    } else if (name === 'changeCard') {
      delete submitData.aitool;
      submitData = { ...submitData.paymentInfo };
    }
    console.log('submitDate : ', submitData);

    try {
      const res = await (isPersonnel
        ? submitPaymentAddPersonnel
        : name === 'changeCard'
        ? submitPaymentInfo
        : submitPayment)(submitData);
      onSubmitCb(res);
      updateStorage();
    } catch (error) {
      console.error('Failed to pay with card', error);
    }
  };

  const updateStorage = async () => {
    try {
      const res = await getStorage();
      dispatch(storageChanged(res.data));
    } catch (error) {
      console.error('Failed to update storage', error);
    }
  };

  const cardInfoList = [
    { name: 'cardNum', label: CARD_NUMBER },
    {
      name: 'cardExp',
      label: CARD_EXPIRATION_DATE,
    },
    { name: 'buyerName', label: NAME },
    { name: 'birthDate', label: BIRTHDATE },
    { name: 'cardPwd', label: PASSWORD },
    {
      name: 'installments',
      label: MONTHLY_INSTALLMENTS,
    },
    {
      name: 'mobile',
      label: MOBILE_NUMBER,
    },
    { name: 'email', label: EMAIL },
  ];

  const yearList = Array.from({ length: 11 }, (_, i) => ({
    nm: `${year + i}`,
    cd: `${year + i}`.substring(2),
  }));

  let installmentsList: any[] = [];
  for (const [key, value] of Object.entries(INSTALLMENTS)) {
    installmentsList.push({ nm: value, cd: key });
  }
  installmentsList.sort((a, b) => a.cd - b.cd);

  return (
    <div className={cx('panel', 'type_register')}>
      {cardInfoList.map(({ name, label }) => (
        <div key={name} className={cx('panel_row')}>
          <span className={cx('sub')}>
            {label}{' '}
            {name === 'birthDate' && (
              <>
                <br />
                <span className={cx('min_txt')}>
                  {CARD_BUSINESS_REGISTRATION_NUMBER}
                </span>
              </>
            )}
          </span>
          <div className={cx('detail')}>
            {name === 'cardNum' ? (
              <div
                className={cx('input_box', {
                  'input--error':
                    (errors as any).paymentInfo?.cardNo1 ||
                    (errors as any).paymentInfo?.cardNo2 ||
                    (errors as any).paymentInfo?.cardNo3 ||
                    (errors as any).paymentInfo?.cardNo4,
                })}
              >
                <span className={cx('blind')}>{CARD_NUMBER}</span>
                <input
                  type={'number'}
                  title={CARD_NUMBER}
                  placeholder={''}
                  inputMode="numeric"
                  autoComplete="off"
                  {...register('paymentInfo.cardNo1', {
                    onChange: ({ target }) => {
                      target.value = limitMaxLen(target.value, 4);
                    },
                    onBlur: () => {
                      checkCardNumberLen();
                    },
                  })}
                />
                <span className={cx('dash_icon')}></span>
                <input
                  type={'number'}
                  title={CARD_NUMBER}
                  placeholder={''}
                  inputMode="numeric"
                  autoComplete="off"
                  {...register('paymentInfo.cardNo2', {
                    onChange: ({ target }) => {
                      target.value = limitMaxLen(target.value, 4);
                    },
                    onBlur: () => {
                      checkCardNumberLen();
                    },
                  })}
                />
                <span className={cx('dash_icon')}></span>
                <input
                  type={'password'}
                  title={CARD_NUMBER}
                  placeholder={''}
                  inputMode="numeric"
                  autoComplete="new-password"
                  {...register('paymentInfo.cardNo3', {
                    onChange: ({ target }) => {
                      target.value = limitMaxLen(target.value, 4);
                    },
                    onBlur: () => {
                      checkCardNumberLen();
                    },
                  })}
                />
                <span className={cx('dash_icon')}></span>
                <input
                  type={'number'}
                  title={CARD_NUMBER}
                  placeholder={''}
                  inputMode="numeric"
                  autoComplete="off"
                  {...register('paymentInfo.cardNo4', {
                    onChange: ({ target }) => {
                      target.value = limitMaxLen(target.value, 4);
                    },
                    onBlur: () => {
                      checkCardNumberLen();
                    },
                  })}
                />
                <span className={cx('msg', 'invalid')}>
                  {(errors as any).paymentInfo?.cardNo1?.message ||
                    (errors as any).paymentInfo?.cardNo2?.message ||
                    (errors as any).paymentInfo?.cardNo3?.message ||
                    (errors as any).paymentInfo?.cardNo4?.message}
                </span>
              </div>
            ) : name === 'cardExp' ? (
              <div
                className={cx('partition_box', {
                  'input--error':
                    (errors as any).paymentInfo?.expMonth?.message ||
                    (errors as any).paymentInfo?.expYear?.message,
                })}
              >
                <span className={cx('blind')}></span>
                <Controller
                  name={'paymentInfo.expMonth'}
                  control={control}
                  defaultValue={getValues('paymentInfo.expMonth')}
                  render={({ field, fieldState }: any) => (
                    <SelectBox
                      name="paymentInfo.expMonth"
                      list={[
                        { nm: `1`, cd: '01' },
                        { nm: `2`, cd: '02' },
                        { nm: `3`, cd: '03' },
                        { nm: `4`, cd: '04' },
                        { nm: `5`, cd: '05' },
                        { nm: `6`, cd: '06' },
                        { nm: `7`, cd: '07' },
                        { nm: `8`, cd: '08' },
                        { nm: `9`, cd: '09' },
                        { nm: `10`, cd: '10' },
                        { nm: `11`, cd: '11' },
                        { nm: `12`, cd: '12' },
                      ]}
                      setValue={(name, value) => {
                        setValue(name, value);
                        clearErrors(name);
                      }}
                      curValue={getValues('paymentInfo.expMonth')}
                      fieldState={fieldState}
                      field={field}
                    />
                  )}
                ></Controller>
                <Controller
                  name={'paymentInfo.expYear'}
                  control={control}
                  defaultValue={getValues('paymentInfo.expYear')}
                  render={({ field, fieldState }: any) => (
                    <SelectBox
                      name="paymentInfo.expYear"
                      list={yearList}
                      setValue={(name, value) => {
                        setValue(name, value);
                        clearErrors(name);
                      }}
                      curValue={getValues('paymentInfo.expYear')}
                      fieldState={fieldState}
                      field={field}
                    />
                  )}
                ></Controller>
                <span className={cx('msg', 'invalid')}>
                  {(errors as any).paymentInfo?.expMonth?.message ||
                    (errors as any).paymentInfo?.expYear?.message}
                </span>
              </div>
            ) : name === 'birthDate' ? (
              <div className={cx('input_box')}>
                <div
                  className={cx('input_box', {
                    'input--error': (errors as any).paymentInfo?.idNo,
                  })}
                >
                  <span className={cx('blind')}></span>
                  <input
                    type="number"
                    title={BIRTHDATE}
                    placeholder={BIRTHDATE_SIX_DIGITS_PLACEHOLDER}
                    inputMode="numeric"
                    autoComplete="off"
                    {...register('paymentInfo.idNo', {
                      onChange: ({ target }) => {
                        target.value = limitMaxLen(target.value, 10);
                      },
                    })}
                  />
                  <span className={cx('msg', 'invalid')}>
                    {(errors as any).paymentInfo?.idNo?.message}
                  </span>
                </div>
              </div>
            ) : name === 'cardPwd' ? (
              <div
                className={cx('input_box', {
                  'input--error': (errors as any)?.paymentInfo?.cardPw,
                })}
              >
                <span className={cx('blind')}></span>
                <input
                  type="password"
                  title={`${PASSWORD} ${CARD_PIN_PREFIX}`}
                  placeholder={CARD_PIN_PREFIX}
                  inputMode="numeric"
                  autoComplete="new-password"
                  {...register('paymentInfo.cardPw', {
                    onChange: ({ target }) => {
                      target.value = limitMaxLen(target.value, 2);
                    },
                  })}
                />
                <i className={cx('svg_i', 'icon_x')}>
                  <span className={cx('blind')}>X</span>
                </i>
                <i className={cx('svg_i', 'icon_x')}>
                  <span className={cx('blind')}>X</span>
                </i>
                <span className={cx('msg', 'invalid')}>
                  {(errors as any)?.paymentInfo?.cardPw?.message}
                </span>
              </div>
            ) : name === 'installments' ? (
              <div
                className={cx('partition_box', {
                  'input--error': (errors as any).paymentInfo?.cardQuota
                    ?.message,
                })}
              >
                <span className={cx('blind')}></span>
                <Controller
                  name={'paymentInfo.cardQuota'}
                  control={control}
                  defaultValue={getValues('paymentInfo.cardQuota')}
                  render={({ field, fieldState }: any) => (
                    <SelectBox
                      name="paymentInfo.cardQuota"
                      list={installmentsList}
                      setValue={(name, value) => {
                        setValue(name, value);
                      }}
                      curValue={getValues('paymentInfo.cardQuota')}
                      field={field}
                      fieldState={fieldState}
                    />
                  )}
                ></Controller>
                <span className={cx('msg', 'invalid')}>
                  {(errors as any).paymentInfo?.cardQuota?.message}
                </span>
              </div>
            ) : name === 'mobile' ? (
              <div
                className={cx('input_box', {
                  'input--error': (errors as any)?.paymentInfo?.buyerTel,
                })}
              >
                <span className={cx('blind')}></span>
                <input
                  type="number"
                  title={MOBILE_NUMBER}
                  placeholder={ENTER_MOBILE_NUMBER}
                  inputMode="numeric"
                  autoComplete="off"
                  {...register('paymentInfo.buyerTel', {
                    onChange: ({ target }) => {
                      target.value = limitMaxLen(target.value, 11);
                    },
                  })}
                />
                <span className={cx('msg', 'invalid')}>
                  {(errors as any)?.paymentInfo?.buyerTel?.message}
                </span>
              </div>
            ) : name === 'email' ? (
              <div
                className={cx('input_box', {
                  'input--error': (errors as any)?.paymentInfo?.buyerEmail,
                })}
              >
                <span className={cx('blind')}></span>
                <input
                  type="text"
                  title={EMAIL}
                  placeholder={ENTER_EMAIL_MESSAGE}
                  autoComplete="off"
                  {...register('paymentInfo.buyerEmail')}
                />
                <span className={cx('msg', 'invalid')}>
                  {(errors as any)?.paymentInfo?.buyerEmail?.message}
                </span>
              </div>
            ) : (
              <div
                className={cx('input_box', {
                  'input--error': (errors as any)?.paymentInfo?.buyerName,
                })}
              >
                <span className={cx('blind')}></span>
                <input
                  type="text"
                  title={NAME}
                  placeholder={ENTER_NAME}
                  autoComplete="off"
                  {...register('paymentInfo.buyerName')}
                />
                <span className={cx('msg', 'invalid')}>
                  {(errors as any)?.paymentInfo?.buyerName?.message}
                </span>
              </div>
            )}
          </div>
        </div>
      ))}

      <div className={cx('access_terms_box')}>
        <h3>{TERMS}</h3>
        <button
          type="button"
          className={cx('btn', 'b_t', 'dull', { on: isAgreeAll })}
          onClick={agreeAllTerms}
        >
          <Icon name="check" classname={cx('svg_check')}></Icon>
          <span>{ACCEPT_ALL_TERMS}</span>
        </button>

        <ul>
          {agreeList.map((item, i) => (
            <Controller
              key={item.agreeCd}
              render={(field: any) => (
                <TermsItem
                  {...field}
                  data={{ ...item }}
                  setValue={setAgreementValue}
                  checked={isCheckedAgreeList[i]}
                />
              )}
              name={`agreeCdList.${i}` as const}
              control={control}
              defaultValue=""
            ></Controller>
          ))}
        </ul>
      </div>

      <div className={cx('guide_text')}>
        <p className={cx('desc')}>※ {AUTOMATIC_PAYMENT_DESC}</p>
        <p>
          <span
            dangerouslySetInnerHTML={{ __html: MORE_DETAILS_SERVICE_REFERENCE }}
          ></span>
        </p>
        {membershipInfo?.paymentPeriod === 'year' && !isPersonnel && (
          <p className={cx('color_point')}>※ {REFUND_INFORMATION}</p>
        )}
      </div>

      <div className={cx('btn_wrap', 'bottom', 'cell')}>
        <button
          type="button"
          className={cx('btn', 'b_t', 'dull', 's44')}
          onClick={() => navigate(-1)}
        >
          <span>{PREV}</span>
        </button>
        <button
          type="button"
          className={cx('btn', 'b_t', 's44', {
            'is--disabled': !isValid || !isAgreeAll,
          })}
          onClick={handleSubmit(onSubmit)}
        >
          <span>{PAYMENT}</span>
        </button>
      </div>
    </div>
  );
}
